// WhatsApp + GA4/GTM conversion tracking utilities.
//
// WhatsApp launcher (Feb 2026):
//   - Always use https://wa.me/<num>?text=... (universal deep link).
//     • On mobile → opens the WhatsApp app via Universal Link / Intent.
//     • On desktop → wa.me itself redirects to web.whatsapp.com (Meta-owned redirect).
//   - We DO NOT use api.whatsapp.com or web.whatsapp.com directly — some
//     corporate desktop networks and browser extensions block those hosts.
//
// GA4 recommended events used:
//   generate_lead  — whatsapp_click, call_click, quote submit, contact form submit
//   view_item      — vehicle detail page view
//   select_content — route detail page view (content group navigation)
//   begin_checkout — fare calculator completed (with computed total value)

import { sendAnalyticsEvent } from "./analytics";
const CONVERSION_META = { event_category: "conversion" };

export const trackEvent = (event, params = {}) => {
    try {
        sendAnalyticsEvent(event, params);
    } catch (_) {
        /* no-op */
    }
};

export const trackConversion = (type, params = {}) => {
    const enriched = { ...CONVERSION_META, ...params };
    switch (type) {
        case "whatsapp_click":
            trackEvent("whatsapp_click", enriched);
            trackEvent("generate_lead", { ...enriched, method: "whatsapp" });
            break;
        case "call_click":
            trackEvent("call_click", enriched);
            trackEvent("generate_lead", { ...enriched, method: "phone" });
            break;
        case "quote_request":
            trackEvent("quote_request", enriched);
            trackEvent("generate_lead", { ...enriched, method: "quote_form" });
            break;
        case "fare_calc_used":
            trackEvent("fare_calc_used", enriched);
            trackEvent("begin_checkout", enriched);
            break;
        case "booking_form_submit":
            trackEvent("booking_form_submit", enriched);
            trackEvent("generate_lead", { ...enriched, method: "booking_form" });
            break;
        case "vehicle_page_view":
            trackEvent("vehicle_page_view", enriched);
            trackEvent("view_item", enriched);
            break;
        case "route_page_view":
            trackEvent("route_page_view", enriched);
            trackEvent("select_content", { ...enriched, content_type: "route" });
            break;
        default:
            trackEvent(type, enriched);
    }
};

// --- Cross-platform WhatsApp helpers -----------------------------------------
export function isMobileUA() {
    if (typeof navigator === "undefined") return false;
    const ua = (navigator.userAgent || "").toLowerCase();
    // Include common mobile identifiers; iPad on iPadOS 13+ reports as Mac so
    // also check for touch-first devices.
    if (/android|iphone|ipod|blackberry|windows phone|iemobile|opera mini/.test(ua)) return true;
    if (/ipad/.test(ua)) return true;
    if (/mac/.test(ua) && navigator.maxTouchPoints > 1) return true;
    return false;
}

const cleanPhone = (phone) => String(phone || "").replace(/\D/g, "");

export function waMeUrl(phone, message) {
    return `https://wa.me/${cleanPhone(phone)}?text=${encodeURIComponent(message || "")}`;
}

/**
 * Returns the WhatsApp deep-link URL. Always wa.me — Meta's universal
 * redirect handles desktop → WhatsApp Web transparently.
 * Exported for use in <a href> anchors where full control is required.
 */
export const buildWhatsAppUrl = (phone, message) => waMeUrl(phone, message);

/**
 * Legacy helper: no longer rewrites hosts. Kept only for backwards
 * compatibility with any imports still calling toPlatformWhatsAppUrl(url).
 */
export function toPlatformWhatsAppUrl(url) {
    return url;
}

export const openWhatsApp = (phone, message, source = "generic") => {
    trackConversion("whatsapp_click", { source });
    const digits = cleanPhone(phone);
    if (!digits) return;
    const url = waMeUrl(phone, message);
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
        // Popup blocked — navigate current tab.
        window.location.href = url;
    }
};

export const callPhone = (phone, source = "generic") => {
    trackConversion("call_click", { source });
    window.location.href = `tel:+${cleanPhone(phone)}`;
};

export const defaultWhatsAppMessage = () =>
    "Hi VVPSS Pvt. Ltd. Taxi Services Team,\nI would like to enquire about a taxi/tempo traveller booking. Please share the best quote.";

